import React from 'react';
import { Button, Text, Badge, ActionIcon, Group, Tooltip, ScrollArea } from '@mantine/core';
import { IconPlus, IconTrash, IconUser, IconCheck, IconAlertTriangle, IconHistory } from '@tabler/icons-react';
import ConsentStatusBadge from './ConsentStatusBadge';
import { getAgeDivision } from '../../services/schoolService';

// The student's own details. Entries (solos or ensembles — a student can be in
// ensembles only) are checked on the review step, not here.
// eslint-disable-next-line no-unused-vars
const isStudentComplete = (student, variations = []) => {
  const di = student.dancer_information;
  return Boolean(di?.first_name && di?.last_name && di?.date_of_birth);
};

const StudentSidebar = ({
  students,
  selectedStudentId,
  onSelectStudent,
  onAddStudent,
  onAddFromLastYear, // shown only when the school has earlier-season students
  onDeleteStudent,
  studentVariations = {},
  optimisticNames = {},
  isFinalized = false,
  isMobile = false
}) => {
  return (
    <div style={{
      width: isMobile ? '100%' : '260px',
      minWidth: isMobile ? 'unset' : '260px',
      borderRight: isMobile ? 'none' : '1px solid #eee',
      borderBottom: isMobile ? '1px solid #eee' : 'none',
      display: 'flex',
      flexDirection: 'column',
      background: '#fafafa',
      height: isMobile ? 'auto' : '100%'
    }}>
      {/* Header */}
      <div style={{ padding: '1rem', borderBottom: '1px solid #eee' }}>
        {!isFinalized && (
          <Button
            fullWidth
            leftSection={<IconPlus size={16} />}
            onClick={onAddStudent}
            style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))' }}
            size="sm"
          >
            Add Student
          </Button>
        )}
        {!isFinalized && onAddFromLastYear && (
          // Outlined in the brand gold so it reads as the second option, not
          // a disabled one (testers skipped the pale "light" variant).
          <Button
            fullWidth
            mt="xs"
            variant="outline"
            leftSection={<IconHistory size={16} />}
            onClick={onAddFromLastYear}
            size="sm"
            styles={{ root: { borderColor: 'var(--primary-gold)', borderWidth: 1.5, color: 'var(--deep-gold)', background: 'white' } }}
          >
            Add from last year
          </Button>
        )}
        <Text size="xs" color="dimmed" mt="xs" ta="center">
          {students.length} student{students.length !== 1 ? 's' : ''} registered
        </Text>
      </div>

      {/* Student List */}
      <ScrollArea style={{ flex: 1 }}>
        {students.length === 0 ? (
          <div style={{ padding: '2rem 1rem', textAlign: 'center' }}>
            <IconUser size={32} color="#ccc" style={{ marginBottom: '0.5rem' }} />
            <Text size="sm" color="dimmed">No students yet.<br />Click "Add Student" to get started.</Text>
          </div>
        ) : (
          students.map((student) => {
            const di = student.dancer_information;
            const optimistic = optimisticNames[student.id];
            const firstName = optimistic?.first_name ?? di?.first_name ?? '';
            const lastName = optimistic?.last_name ?? di?.last_name ?? '';
            const name = firstName || lastName ? `${firstName} ${lastName}`.trim() : 'New Student';
            const ageDivision = di?.date_of_birth ? getAgeDivision(di.date_of_birth) : null;
            const isSelected = student.id === selectedStudentId;
            const vars = studentVariations[student.id] || [];
            const hasAnyData = !!(firstName || lastName);
            const complete = isStudentComplete(student, vars);

            return (
              <div
                key={student.id}
                onClick={() => onSelectStudent(student.id)}
                style={{
                  padding: '0.75rem 1rem',
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee',
                  background: isSelected ? 'linear-gradient(135deg, rgba(212,165,116,0.15), rgba(196,149,99,0.1))' : 'transparent',
                  borderLeft: isSelected ? '3px solid var(--primary-gold)' : '3px solid transparent',
                  transition: 'all 0.15s ease',
                  position: 'relative'
                }}
              >
                <Group gap={4} wrap="nowrap" mb={4}>
                  <Text
                    size="sm"
                    fw={isSelected ? 700 : 400}
                    style={{
                      color: isSelected ? 'var(--deep-gold)' : 'var(--charcoal)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1
                    }}
                  >
                    {name}
                  </Text>
                  {hasAnyData && (
                    <Tooltip label={complete ? 'Profile complete' : 'Missing required info'} withArrow position="right">
                      {complete
                        ? <IconCheck size={14} color="green" style={{ flexShrink: 0 }} />
                        : <IconAlertTriangle size={14} color="#f59e0b" style={{ flexShrink: 0 }} />
                      }
                    </Tooltip>
                  )}
                </Group>
                <Group gap={4} wrap="nowrap" align="center">
                  {ageDivision && (
                    <Badge size="xs" variant="light" color="yellow">{ageDivision}</Badge>
                  )}
                  <ConsentStatusBadge status={student.parent_consent_status || 'not_required'} />
                  <div style={{ flex: 1 }} />
                  {!isFinalized && (
                    <Tooltip label="Remove student" withArrow>
                      <ActionIcon
                        size="xs"
                        color="red"
                        variant="subtle"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Remove ${name}? This cannot be undone.`)) {
                            onDeleteStudent(student.id);
                          }
                        }}
                      >
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </div>
            );
          })
        )}
      </ScrollArea>
    </div>
  );
};

export default StudentSidebar;
